import { useMemo } from 'react';
import { useSearchQuery } from '../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationPublicRpc.hooks';
import getLang from 'I18n/utils/getLang';
import {
  FilterGroup,
  ListingCard,
} from '../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc';

interface SearchByListingIdParams {
  searchIds: number[];
  searchBy: 'LISTING_ID';
}

interface SearchBySourceIdParams {
  searchIds: string[];
  searchBy: 'SOURCE_ID';
}

interface UseGetStorefrontListingsProps {
  searchParams: SearchByListingIdParams | SearchBySourceIdParams;
  skip?: boolean;
}

interface UseGetStorefrontListingsResponse {
  listingCards: ListingCard[];
  error?: Error;
  loading: boolean;
}

export function useGetStorefrontListings({
  searchParams,
  skip,
}: UseGetStorefrontListingsProps): UseGetStorefrontListingsResponse {
  const listingIdQuery =
    searchParams.searchBy === 'LISTING_ID'
      ? [
          {
            filtersByField: {
              LISTING_ID: [
                {
                  __typename:
                    'com.hubspot.marketplace.search.models.filters.LongFilterQuery',
                  values: searchParams.searchIds || [],
                  negation: false,
                  clause: 'OR',
                },
              ],
            },
            clause: 'OR',
            negation: false,
          } as FilterGroup,
        ]
      : [];
  const sourceIdQuery =
    searchParams.searchBy === 'SOURCE_ID'
      ? [
          {
            filtersByField: {
              SOURCE_ID: [
                {
                  __typename:
                    'com.hubspot.marketplace.search.models.filters.StringFilterQuery',
                  values: searchParams.searchIds || [],
                  negation: false,
                  clause: 'OR',
                },
              ],
            },
            clause: 'AND',
            negation: false,
          } as FilterGroup,
        ]
      : [];

  const { data, loading, error } = useSearchQuery({
    variables: {
      queryType: 'ELASTIC_SEARCH',
      language: getLang(),
      filter: {
        filterGroups: [...listingIdQuery, ...sourceIdQuery],
        clause: 'OR',
      },
    },
    skip: skip || !searchParams,
  });

  const listingCards = useMemo(
    () => (loading ? [] : data?.cards),
    [data?.cards, loading]
  );

  return { listingCards: listingCards || [], loading, error };
}
